/**
 * voice-v8 — welche Endwörter der Mitschrift aus dem Rauschen kamen. (03.10.2026)
 *
 * Warum: shared/rausch-woerter.ts. Hier das Messen in der App.
 *
 * Ein Endwort gilt als Rauschen, wenn BEIDES stimmt:
 *   1. es begann in der laufenden Stille — mindestens RAUSCH_RAND_MS nach
 *      ihrem Beginn (der Pegel der Karte, dieselbe Stille wie die Entscheidung);
 *   2. jedes gemessene Bild in seinem Zeitraum liegt unter der Stille-Grenze
 *      dieses Bildes (Grenze folgt dem Grundpegel, mic-visualizer.ts).
 * Gezählt wird nur die Reihe am Ende, ohne Lücke.
 *
 * Die Zeitmarken (Soniox `start_ms`/`end_ms`) rechnet live-dictation.ts auf
 * die Uhr der Seite um (`uhrStartMs`/`uhrEndMs`). Steht deren Rechentakt
 * zeitweise, liegen die Marken zu FRÜH — dann fällt das Wort vor den Beginn
 * der Stille und zählt nicht als Rauschen (die sichere Richtung).
 *
 * ⚠️ Nur messen und dem Urteil sagen. Der Pegel wird hier nur GELESEN, was die
 *    Stille misst (merkeStille in mic-visualizer.ts), bleibt unberührt.
 * ⚠️ Ohne Zeitmarken, ohne Bild oder ohne Grundpegel (Grenze unendlich): kein
 *    Rauschen — im Zweifel das alte Verhalten.
 */

import { normWort } from "../../shared/sende-wort.js";
import { RAUSCH_HOECHSTENS, type RauschMessung } from "../../shared/rausch-woerter.js";
import type { LiveWort } from "./jetzt-senden.js";

/** So weit nach dem Beginn der Stille muss ein Wort beginnen, um als Rauschen zu gelten. */
export const RAUSCH_RAND_MS = 150;
/** So lange hält der Verlauf Bilder vor. */
const PEGEL_VERLAUF_MS = 15_000;
/** Liegt das Ende eines Wortes so weit hinter dem letzten Bild, ist es (noch) nicht gemessen. */
const NACH_LETZTEM_BILD_MS = 100;

interface PegelVerlauf {
  /** Ein Bild der Anzeige — nur wirklich gemessene (`roh` eine Zahl) zählen. */
  merke(t: number, roh: number | null | undefined, grenze: number | undefined): void;
  /**
   * Höchster Pegel im Zeitraum und ob ALLE Bilder darin unter ihrer Grenze
   * lagen. `null`: kein gemessenes Bild darin, das Wort reicht über das letzte
   * Bild hinaus, oder eine Grenze war noch unbekannt.
   */
  spitze(von: number, bis: number): { roh: number; leise: boolean } | null;
}

export function neuerPegelVerlauf(): PegelVerlauf {
  const bilder: { t: number; roh: number; grenze: number }[] = [];
  return {
    merke(t, roh, grenze) {
      if (typeof roh !== "number" || !Number.isFinite(roh)) return;
      bilder.push({ t, roh, grenze: typeof grenze === "number" ? grenze : Number.POSITIVE_INFINITY });
      while (bilder.length > 0 && t - (bilder[0]?.t ?? t) > PEGEL_VERLAUF_MS) bilder.shift();
    },
    spitze(von, bis) {
      const letztes = bilder.at(-1);
      if (!letztes || bis > letztes.t + NACH_LETZTEM_BILD_MS) return null;
      let roh = -1;
      let leise = true;
      for (const b of bilder) {
        if (b.t < von || b.t > bis) continue;
        if (!Number.isFinite(b.grenze)) return null;
        roh = Math.max(roh, b.roh);
        if (b.roh > b.grenze) leise = false;
      }
      return roh < 0 ? null : { roh, leise };
    },
  };
}

/** Ein Endwort mit seinen Messwerten. */
interface EndWort {
  pegel: number | null;
  konf: number | null;
  still: boolean | null;
  rausch: boolean;
}

/**
 * Wie viele Endwörter von `text` aus dem Rauschen kamen — samt Messung je Wort.
 *
 * `woerter`: die Wörter der Erkennung mit Uhrzeiten (bestätigte und vorläufige,
 * in Reihenfolge). `stilleBeginn`: wann die laufende Stille begann (Uhr der
 * Seite) — `null`, wenn gerade keine läuft oder der Pegel nicht misst.
 *
 * ⚠️ Passen die Endwörter nicht zum Text (Jev-Schreibweise, zusammengefügte
 *    Teilstücke), ist `k` 0. Ebenso, wenn sonst nichts übrig bliebe.
 */
export function rauschBefund(
  text: string,
  woerter: readonly LiveWort[],
  verlauf: PegelVerlauf,
  stilleBeginn: number | null,
  grenze: number | null = null,
): RauschMessung {
  const ende = woerter.slice(-RAUSCH_HOECHSTENS);
  const je: EndWort[] = ende.map((w) => {
    const von = w.uhrStartMs ?? null;
    const bis = w.uhrEndMs ?? von;
    const s = von !== null && bis !== null ? verlauf.spitze(von, Math.max(von, bis)) : null;
    const still = von === null || stilleBeginn === null ? null : von >= stilleBeginn + RAUSCH_RAND_MS;
    return { pegel: s ? Math.round(s.roh * 1000) / 1000 : null, konf: w.konfidenz ?? null, still, rausch: still === true && s?.leise === true };
  });
  let k = 0;
  while (k < je.length && je[je.length - 1 - k]?.rausch === true) k++;
  if (k > 0) {
    const teile = text.trim().split(/\s+/u).filter(Boolean);
    const passt =
      k < teile.length &&
      normWort(teile.slice(-k).join(" ")) === normWort(ende.slice(-k).map((w) => w.text).join(" "));
    if (!passt) k = 0;
  }
  return {
    k,
    pegel: je.map((w) => w.pegel),
    konf: je.map((w) => w.konf),
    still: je.map((w) => w.still),
    grenze: grenze !== null && Number.isFinite(grenze) ? Math.round(grenze * 1000) / 1000 : null,
  };
}
